import { SUPPORTED_LANGUAGES } from "i18n";
import React from "react";
import { Helmet } from "react-helmet";
import { Redirect, Route, Switch } from "react-router-dom";
import { IRoute } from "router";

import { testProp } from "@/browser";
import { AppErrorBoundary, AppSuspense } from "@/components/app";
import { FeatureFlagsProvider } from "@/components/FeatureFlags";
import {
  ConsultantProfileProvider,
  ContextSwitcher,
  LoginRequired,
  UserContextConsumer,
  UserContextProvider,
} from "@/components/User";
import { useOnLanguageChange } from "@/hooks/Translations";
import { MessengerProvider } from "@/infrastructure/Messenger";
import { TaskContextProvider } from "@/infrastructure/Tasks";
import { AgreementGuard } from "@/views/components/AgreementsGuard";
import { AnimatedLayout, Layout } from "@/views/components/Layout";
import { LanguageRouteHandler } from "@/views/LanguageRouteHandler";

import { isProduction } from "../core/environments";
import { routes as chatRoutes } from "../views-new/chat/router";
import { routes as companiesRoutes } from "../views-new/companies/router";
import { routes as dashboardRoutes } from "../views-new/dashboard/router";
import { routes as notificationsRoutes } from "../views-new/notifications/router";
import { routes as adminInvitationsRoutes } from "./AdminInvitations/router";
import { routes as assignmentsRoutes } from "./Assignments/router";
import { publicRoutes as clientPublicRoutes, routes as clientRoutes } from "./Clients/router";
import { CookiesInfo } from "./components/CookiesInfo";
import { SessionResolver } from "./components/Session";
import { routes as consultantInvitationsRoutes } from "./ConsultantInvitations/router";
import { routes as consultantProfilesRoutes } from "./ConsultantProfiles/router";
import { routes as contractRoutes } from "./Contracts/router";
import { Error4XX } from "./Errors";
import { routes as jobApplicationsRoutes } from "./job-applications/router";
import { routes as jobRequestsRoutes } from "./JobRequests/router";
import { routes as landingRoutes } from "./Landing/router";
import { routes as managerInvitationsRoutes } from "./ManagerInvitations/router";
import { Onboarding } from "./Onboarding";
import { routes as onboardingRoutes } from "./Onboarding/router";
import { routes as personalInvitationsRoutes } from "./PersonalInvitations/router";
import { router } from "./router";
import { routes as sessionRoutes } from "./Session/router";
import { routes as userRoutes } from "./Settings/router";
import { routes as tasksRoutes } from "./Tasks/router";
import { routes as timeReportsRoutes } from "./time-reports/router";
import { pages as applicationPages } from "./views";
import { routes as workOrdersRoutes } from "./WorkOrders/router";

const Language = () => {
  const currentLanguage = useOnLanguageChange();

  return (
    <Helmet>
      <html lang={currentLanguage} />
    </Helmet>
  );
};

const lngParam = `/:lng(${SUPPORTED_LANGUAGES.join("|")})?`;

export class Pages extends React.Component {
  renderRoutes(routes: IRoute[], shouldBeVisible: (route: IRoute) => boolean = () => true) {
    return routes.filter(shouldBeVisible).map(this.renderRoute);
  }

  renderLangRoutes(routes: IRoute[], shouldBeVisible: (route: IRoute) => boolean = () => true) {
    return routes
      .filter(shouldBeVisible)
      .map(route => ({ ...route, path: lngParam + route.path, hrefLang: true }))
      .map(this.renderRoute);
  }

  renderRoute(route: IRoute, index: number) {
    return (
      <Route
        key={index}
        exact={route.exact}
        path={route.path}
        render={(props: any) => {
          const Component = applicationPages[route.name];

          return (
            <AppErrorBoundary key={route.name}>
              <AppSuspense>
                {route.hrefLang && (
                  <>
                    <Helmet>
                      {SUPPORTED_LANGUAGES.map(locale => (
                        <link
                          rel="alternate"
                          href={`${window.location.origin}${route.path.replace(lngParam, `/${locale}`)}`}
                          hrefLang={locale}
                          key={locale}
                        />
                      ))}
                    </Helmet>
                    <LanguageRouteHandler route={route} />
                  </>
                )}
                <Component {...props} {...testProp(route)} />
              </AppSuspense>
            </AppErrorBoundary>
          );
        }}
      />
    );
  }

  render() {
    return (
      <Switch>
        <Route
          exact
          path={[
            ...sessionRoutes,
            ...managerInvitationsRoutes,
            ...adminInvitationsRoutes,
            ...consultantInvitationsRoutes,
            ...personalInvitationsRoutes,
          ].map(r => lngParam + r.path)}>
          <AnimatedLayout>
            {this.renderLangRoutes(sessionRoutes)}
            {this.renderLangRoutes(managerInvitationsRoutes)}
            {this.renderLangRoutes(adminInvitationsRoutes)}
            {this.renderLangRoutes(consultantInvitationsRoutes)}
            {this.renderLangRoutes(personalInvitationsRoutes)}
          </AnimatedLayout>
        </Route>
        <SessionResolver>
          <>
            <Helmet defaultTitle="Ework Verama" />
            <Language />
            <CookiesInfo />
            <UserContextProvider>
              <UserContextConsumer>
                {({ hasRouteAccess }) => {
                  return (
                    <Switch>
                      {this.renderLangRoutes(landingRoutes)}
                      {this.renderLangRoutes(clientPublicRoutes)}
                      <AgreementGuard>
                        <LoginRequired>
                          {({ isAuthenticated }) => {
                            return isAuthenticated ? (
                              <FeatureFlagsProvider>
                                <Switch>
                                  {this.renderRoutes(onboardingRoutes, hasRouteAccess)}
                                  <Onboarding>
                                    <ContextSwitcher>
                                      <ConsultantProfileProvider>
                                        <MessengerProvider>
                                          <TaskContextProvider>
                                            <Layout topbar showNavigation footer>
                                              <Switch>
                                                {this.renderRoutes(clientRoutes, hasRouteAccess)}
                                                {this.renderRoutes(dashboardRoutes, hasRouteAccess)}
                                                {this.renderRoutes(companiesRoutes, hasRouteAccess)}
                                                {this.renderRoutes(notificationsRoutes, hasRouteAccess)}
                                                {this.renderRoutes(chatRoutes, hasRouteAccess)}
                                                {this.renderRoutes(consultantProfilesRoutes, hasRouteAccess)}
                                                {this.renderRoutes(jobApplicationsRoutes, hasRouteAccess)}
                                                {this.renderRoutes(jobRequestsRoutes, hasRouteAccess)}
                                                {this.renderRoutes(assignmentsRoutes, hasRouteAccess)}
                                                {this.renderRoutes(workOrdersRoutes, hasRouteAccess)}
                                                {this.renderRoutes(tasksRoutes, hasRouteAccess)}
                                                {this.renderRoutes(userRoutes, hasRouteAccess)}
                                                {this.renderRoutes(contractRoutes, hasRouteAccess)}
                                                {!isProduction && this.renderRoutes(timeReportsRoutes, hasRouteAccess)}
                                                <Redirect to={router.get("dashboard").path} />
                                                <Route render={() => <Error4XX />} />
                                              </Switch>
                                            </Layout>
                                          </TaskContextProvider>
                                        </MessengerProvider>
                                      </ConsultantProfileProvider>
                                    </ContextSwitcher>
                                  </Onboarding>
                                </Switch>
                              </FeatureFlagsProvider>
                            ) : (
                              <Switch>
                                <Route
                                  exact
                                  path={router.get("job-requests").path}
                                  render={({ location, match }) => {
                                    return (
                                      <Redirect
                                        to={`${router.get("public-job-requests", match.params).path}${location.search}`}
                                        push
                                      />
                                    );
                                  }}
                                />
                                <Route
                                  exact
                                  path={router.get("job-requests.detail").path}
                                  render={({ location, match }) => {
                                    return (
                                      <Redirect
                                        to={`${router.get("public-job-requests.detail", match.params).path}${
                                          location.search
                                        }`}
                                        push
                                      />
                                    );
                                  }}
                                />
                                <Route
                                  exact
                                  path={router.get("client").path}
                                  render={({ location, match }) => {
                                    return (
                                      <Redirect
                                        to={`${router.get("public-client", match.params).path}${location.search}`}
                                        push
                                      />
                                    );
                                  }}
                                />
                                <Route
                                  exact
                                  path={router.get("client.news-detail").path}
                                  render={({ location, match }) => {
                                    return (
                                      <Redirect
                                        to={`${router.get("public-client.news-detail", match.params).path}${
                                          location.search
                                        }`}
                                        push
                                      />
                                    );
                                  }}
                                />
                                <Route
                                  exact
                                  path={router.get("news.news-detail").path}
                                  render={({ location, match }) => {
                                    return (
                                      <Redirect
                                        to={`${router.get("public-news.news-detail", match.params).path}${
                                          location.search
                                        }`}
                                        push
                                      />
                                    );
                                  }}
                                />
                                <Redirect to={router.get("login").path} />
                                <Route
                                  render={() => (
                                    <AnimatedLayout>
                                      <Error4XX />
                                    </AnimatedLayout>
                                  )}
                                />
                              </Switch>
                            );
                          }}
                        </LoginRequired>
                      </AgreementGuard>
                    </Switch>
                  );
                }}
              </UserContextConsumer>
            </UserContextProvider>
          </>
        </SessionResolver>
      </Switch>
    );
  }
}
